import { apiFetch, API_BASE } from '../api/client'
// Live weather layer helpers — colors shared by MapView + Legend, and the
// fetch for the current per-region weather snapshot served by PR_API.

// Weather type → fill color. Keys must match weather_types.name seeded by
// weather_engine.sql (and returned by pr_weather_classify).
export const WEATHER_COLORS = {
  'Ensoleillé':       '#FDB813',
  'Nuageux':          '#969696',
  'Couvert':          '#a3cdff',
  'Pluie':            '#4A90D9',
  'Forte pluie':      '#2C6FB5',
  'Neige':            '#E8F4FF',
  'Brouillard':       '#4b4b4b',
  'Orage':            '#6A5ACD',
  'Tempête':          '#7B4F9D',
  'Canicule':         '#E24A4A',
  'Blizzard':         '#78ffc0',
  'Tempête de sable': '#694f1f',
  'Cyclone':          '#C13584',
}

export const WEATHER_UNKNOWN_COLOR = '#3a3a3a'

// Build a MapLibre `match` expression coloring regions by their weather_type
// feature property (merged in by MapView from the /map/weather payload).
export const buildWeatherColorExpression = () => {
  const expr = ['match', ['get', 'weather_type']]
  Object.entries(WEATHER_COLORS).forEach(([type, color]) => {
    expr.push(type, color)
  })
  expr.push(WEATHER_UNKNOWN_COLOR) // fallback (no weather data yet)
  return expr
}

// Colors for the moving weather-system overlay (masses d'air).
export const SYSTEM_COLORS = {
  depression: '#3B7DD8',   // low pressure — blue
  anticyclone: '#E0873A',  // high pressure — orange
}

// Fetch the live weather snapshot. Returns the full payload
// { regions: { region_id: {...} }, systems: [...], effects: { type: {...} } }
// or null on failure (map simply stays neutral for the weather layer).
export async function fetchWeather() {
  try {
    const resp = await apiFetch(`${API_BASE}/map/weather`)
    if (!resp.ok) return null
    const body = await resp.json()
    if (!body.success) return null
    const d = body.data || {}
    return { regions: d.regions || {}, systems: d.systems || [], effects: d.effects || {} }
  } catch {
    return null
  }
}

// Available (year, month) pairs in the weather archive, for the timeline.
export async function fetchWeatherHistoryMonths() {
  try {
    const resp = await apiFetch(`${API_BASE}/map/weather/history/months`, { cacheTtl: 600000 })
    if (!resp.ok) return []
    const body = await resp.json()
    return body?.success ? (body.data?.months || []) : []
  } catch {
    return []
  }
}

// Archived weather for a past RP month, same { regions: {...} } shape as
// fetchWeather so the map layer / popups can consume it interchangeably.
export async function fetchWeatherHistory(year, month) {
  try {
    const resp = await apiFetch(`${API_BASE}/map/weather/history?year=${year}&month=${month}`, { cacheTtl: 600000 })
    if (!resp.ok) return null
    const body = await resp.json()
    if (!body?.success) return null
    return { regions: body.data?.regions || {}, systems: [], effects: {} }
  } catch {
    return null
  }
}

// Ghost forecast — one generation ahead. Same { regions } shape; carries the
// predicted (year, month). A prediction only: the real next tick may differ.
export async function fetchWeatherForecast() {
  try {
    const resp = await apiFetch(`${API_BASE}/map/weather/forecast`, { cacheTtl: 120000 })
    if (!resp.ok) return null
    const body = await resp.json()
    if (!body?.success) return null
    const d = body.data || {}
    if (!d.year || !d.month || !Object.keys(d.regions || {}).length) return null
    return { regions: d.regions, systems: [], effects: {}, year: d.year, month: d.month, forecast: true }
  } catch {
    return null
  }
}

// Turn the systems list into a GeoJSON FeatureCollection of circle polygons
// (approximated as N-gons in lon/lat degrees) for a MapLibre fill overlay.
export function systemsToGeoJSON(systems) {
  const features = (systems || []).map((s) => {
    const steps = 40
    const ring = []
    for (let i = 0; i <= steps; i++) {
      const a = (i / steps) * 2 * Math.PI
      // scale lon by cos(lat) so the circle isn't stretched near the poles
      const lon = s.lon + (s.radius_deg * Math.cos(a)) / Math.max(0.2, Math.cos((s.lat * Math.PI) / 180))
      const lat = s.lat + s.radius_deg * Math.sin(a)
      ring.push([lon, lat])
    }
    return {
      type: 'Feature',
      properties: { kind: s.kind, intensity: s.intensity, name: s.name },
      geometry: { type: 'Polygon', coordinates: [ring] },
    }
  })
  return { type: 'FeatureCollection', features }
}
